import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import { resolveMediaUrl } from '../../../utils/mediaUrl'
import { useStudentsEntryScreen } from './hooks/useStudentsEntryScreen'
import { StudentTabs } from './components/StudentTabs'
import { CourseTab } from './components/tabs/CourseTab'
import { FeesTab } from './components/tabs/FeesTab'
import { ReceiptTab } from './components/tabs/ReceiptTab'
import { TextField, SelectField, TextAreaField, PhotoUploadField, CheckboxField } from './components/FieldComponents'
import StudentAttendanceScreen from './StudentAttendanceScreen'
import StudentBiometricScreen from './StudentBiometricScreen'
import StudentDocumentScreen from './StudentDocumentScreen'
import StudentGeneratePasswordScreen from './StudentGeneratePasswordScreen'
import StudentInventoryScreen from './StudentInventoryScreen'
import StudentLedgerScreen from './StudentLedgerScreen'
import StudentLedgerInstallmentsScreen from './StudentLedgerInstallmentsScreen'
import StudentMessagesScreen from './StudentMessagesScreen'
import StudentPerformanceScreen from './StudentPerformanceScreen'
import StudentReceiptScreen from './StudentReceiptScreen'
import StudentRemarksScreen from './StudentRemarksScreen'
import StudentReportCardScreen from './StudentReportCardScreen'
import StudentSendReportEmailScreen from './StudentSendReportEmailScreen'

function FormCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm">
      <h3 className="mb-4 text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2.5">
        {title}
      </h3>
      {children}
    </div>
  )
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="grid gap-1">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">{label}</span>
      <div className="min-h-9 rounded-xl border border-outline-variant/20 bg-surface-container-low px-3 py-2 text-xs font-semibold text-on-surface flex items-center">
        {value || '—'}
      </div>
    </div>
  )
}

function ReadOnlyCheckbox({ label, checked }) {
  return (
    <div className="flex items-center gap-2.5 cursor-default mt-6 select-none">
      <input
        type="checkbox"
        checked={!!checked}
        readOnly
        className="h-4 w-4 rounded-md border-outline-variant text-primary"
      />
      <span className="text-xs font-semibold text-on-surface-variant">{label}</span>
    </div>
  )
}

function ReadOnlyPhoto({ value }) {
  const photoSrc = resolveMediaUrl(value)
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">Student Photo</span>
      <div className="h-32 w-32 overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-low shadow-sm">
        {photoSrc ? (
          <img src={photoSrc} alt="Student Profile" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-on-surface-variant/40">
            <span className="material-symbols-outlined text-3xl">person</span>
            <span className="mt-1 text-[9px] font-bold uppercase tracking-wider">No Photo</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default function StudentsEntryScreen() {
  const { state, actions } = useStudentsEntryScreen()
  const navigate = useNavigate()
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const initialTab = urlParams.get('tab') || 'student-detail'
  const mode = (urlParams.get('mode') || '').toLowerCase()
  const [activeTab, setActiveTab] = useState(initialTab)

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  // Update activeTab when URL tab parameter changes
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tabFromUrl = params.get('tab') || 'student-detail'
    setActiveTab(tabFromUrl)
  }, [location])

  const getNextTab = () => {
    if (activeTab === 'student-detail') return 'course'
    if (activeTab === 'course') return 'fees'
    if (activeTab === 'fees') return 'receipt'
    return 'student-detail'
  }

  const view = (new URLSearchParams(location.search).get('view') || '').toLowerCase()
  if (view === 'attendance') return <StudentAttendanceScreen />
  if (view === 'biometric') return <StudentBiometricScreen />
  if (view === 'messages') return <StudentMessagesScreen />
  if (view === 'receipt') return <StudentReceiptScreen />
  if (view === 'ledger') return <StudentLedgerScreen />
  if (view === 'ledger-installments') return <StudentLedgerInstallmentsScreen />
  if (view === 'remarks') return <StudentRemarksScreen />
  if (view === 'inventory') return <StudentInventoryScreen />
  if (view === 'document') return <StudentDocumentScreen />
  if (view === 'performance') return <StudentPerformanceScreen />
  if (view === 'report-card') return <StudentReportCardScreen />
  if (view === 'send-report-email') return <StudentSendReportEmailScreen />
  if (view === 'generate-password') return <StudentGeneratePasswordScreen />

  if (state.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1320px] items-center justify-center rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-6 py-16 text-sm font-semibold text-on-surface-variant">
        <span className="material-symbols-outlined mr-2 animate-spin text-primary">progress_activity</span>
        Loading student details...
      </div>
    )
  }

  const f = state.form
  const patch = actions.patchForm
  const isView = state.isViewMode
  const opt = state.options || {}

  const renderReadOnlyProfile = () => (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="space-y-6 lg:col-span-7">
        <FormCard title="Student Details">
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <ReadOnlyField label="First Name" value={f.firstName} />
              <ReadOnlyField label="Middle Name" value={f.middleName} />
              <ReadOnlyField label="Last Name" value={f.lastName} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <ReadOnlyCheckbox label="Email" checked={f.sendEmail} />
                <ReadOnlyField label="Email" value={f.email} />
              </div>
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <ReadOnlyCheckbox label="Send SMS" checked={f.sendSms} />
                <ReadOnlyField label="Mobile No" value={f.mobile} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ReadOnlyField label="Landline No." value={f.landline} />
              <ReadOnlyField label="Birth Date" value={f.dob} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ReadOnlyField label="Gender" value={f.gender === 'Select' ? '' : f.gender} />
              <ReadOnlyField label="Religion/Caste" value={f.caste === 'Select' ? '' : f.caste} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ReadOnlyField label="Sub Caste" value={f.subCaste === 'Select' ? '' : f.subCaste} />
              <ReadOnlyField label="Birth Place" value={f.birthPlace} />
            </div>
            <ReadOnlyField label="Language" value={f.language === 'Select' ? '' : f.language} />
          </div>
        </FormCard>

        <FormCard title="Father / Guardian 1">
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ReadOnlyField label="Name" value={f.gName} />
              <ReadOnlyField label="Relation" value={f.gRelation === 'Select' ? '' : f.gRelation} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <ReadOnlyCheckbox label="Email" checked={f.gSendEmail} />
                <ReadOnlyField label="Email" value={f.gEmail} />
              </div>
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <ReadOnlyCheckbox label="Mobile" checked={f.gSendSms} />
                <ReadOnlyField label="Mobile No" value={f.gMobile} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ReadOnlyField label="Phone" value={f.gLandline} />
              <ReadOnlyField label="Occupation" value={f.gOccupation === 'Select' ? '' : f.gOccupation} />
            </div>
            <ReadOnlyField label="Income" value={f.gIncome || '0.00'} />
          </div>
        </FormCard>

        <FormCard title="Mother / Guardian 2">
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ReadOnlyField label="Name" value={f.g2Name} />
              <ReadOnlyField label="Relation" value={f.g2Relation === 'Select' ? '' : f.g2Relation} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <ReadOnlyCheckbox label="Email" checked={f.g2SendEmail} />
                <ReadOnlyField label="Email" value={f.g2Email} />
              </div>
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <ReadOnlyCheckbox label="Mobile" checked={f.g2SendSms} />
                <ReadOnlyField label="Mobile No" value={f.g2Mobile} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ReadOnlyField label="Phone" value={f.g2Landline} />
              <ReadOnlyField label="Occupation" value={f.g2Occupation === 'Select' ? '' : f.g2Occupation} />
            </div>
            <ReadOnlyField label="Income" value={f.g2Income || '0.00'} />
          </div>
        </FormCard>

        <FormCard title="Remarks">
          <div className="grid gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">Remark</span>
            <div className="min-h-[64px] rounded-xl border border-outline-variant/20 bg-surface-container-low px-3 py-2 text-xs text-on-surface">
              {f.particulars || '—'}
            </div>
          </div>
        </FormCard>
      </div>

      <div className="space-y-6 lg:col-span-5">
        <FormCard title="Address">
          <div className="space-y-3.5">
            <ReadOnlyField label="Line 1" value={f.addressLine1} />
            <ReadOnlyField label="Line 2" value={f.addressLine2} />
            <ReadOnlyField label="Line 3" value={f.addressLine3} />
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="City" value={f.city} />
              <ReadOnlyField label="Pin Code" value={f.pinCode} />
            </div>
            <ReadOnlyField label="State" value={f.state === 'Select' ? '' : f.state} />
            <ReadOnlyField label="Place" value={f.place === 'Select' ? '' : f.place} />
          </div>
        </FormCard>

        <FormCard title="School / College">
          <div className="space-y-3.5">
            <ReadOnlyField label="Name" value={f.pSchool === 'Select' ? '' : f.pSchool} />
            <ReadOnlyField label="Prev.Course" value={f.courseStd === 'Select' ? '' : f.courseStd} />
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Maths" value={f.marks} />
              <ReadOnlyField label="Science" value={f.science} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Marks %" value={f.marksPercent} />
              <ReadOnlyField label="Status" value={f.academicStatus || 'Passed'} />
            </div>
            <ReadOnlyField label="Board" value={f.prevBoard === 'Select' ? '' : f.prevBoard} />
            <ReadOnlyField label="Medium" value={f.medium === 'Select' ? '' : f.medium} />
          </div>
        </FormCard>

        <FormCard title="Photo">
          <ReadOnlyPhoto value={f.photoUrl} />
        </FormCard>
      </div>
    </div>
  )

  const renderEditableStudentDetail = () => (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="space-y-6 lg:col-span-7">
        <FormCard title="Student Details">
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <TextField label="First Name" required value={f.firstName} onChange={(v) => patch('firstName', v)} />
              <TextField label="Middle Name" value={f.middleName} onChange={(v) => patch('middleName', v)} />
              <TextField label="Last Name" required value={f.lastName} onChange={(v) => patch('lastName', v)} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <CheckboxField label="Email" checked={f.sendEmail} onChange={(v) => patch('sendEmail', v)} />
                <TextField label="Email" value={f.email} onChange={(v) => patch('email', v)} type="email" />
              </div>
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <CheckboxField label="Send SMS" checked={f.sendSms} onChange={(v) => patch('sendSms', v)} />
                <TextField label="Mobile No" required value={f.mobile} onChange={(v) => patch('mobile', v)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <TextField label="Landline No." value={f.landline} onChange={(v) => patch('landline', v)} />
              <TextField label="Birth Date" value={f.dob} onChange={(v) => patch('dob', v)} type="date" />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SelectField label="Gender" required value={f.gender} onChange={(v) => patch('gender', v)} options={['Select', 'Male', 'Female', 'Other']} />
              <SelectField label="Religion/Caste" value={f.caste} onChange={(v) => patch('caste', v)} options={opt.caste && opt.caste.length ? opt.caste : ['Select', 'General', 'OBC', 'SC', 'ST', 'Other']} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SelectField label="Sub Caste" value={f.subCaste} onChange={(v) => patch('subCaste', v)} options={opt.subcaste && opt.subcaste.length ? opt.subcaste : ['Select', 'Maratha', 'Brahmin', 'Other']} />
              <TextField label="Birth Place" value={f.birthPlace} onChange={(v) => patch('birthPlace', v)} />
            </div>
            <SelectField label="Language" value={f.language} onChange={(v) => patch('language', v)} options={opt.language && opt.language.length ? opt.language : ['Select', 'English', 'Hindi', 'Marathi', 'Gujarati']} />
          </div>
        </FormCard>

        <FormCard title="Father / Guardian 1">
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <TextField label="Name" value={f.gName} onChange={(v) => patch('gName', v)} />
              <SelectField label="Relation" value={f.gRelation} onChange={(v) => patch('gRelation', v)} options={opt.relations && opt.relations.length ? opt.relations : ['Select', 'Father', 'Mother', 'Guardian']} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <CheckboxField label="Email" checked={f.gSendEmail} onChange={(v) => patch('gSendEmail', v)} />
                <TextField label="Email" value={f.gEmail} onChange={(v) => patch('gEmail', v)} type="email" />
              </div>
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <CheckboxField label="Mobile" checked={f.gSendSms} onChange={(v) => patch('gSendSms', v)} />
                <TextField label="Mobile No" value={f.gMobile} onChange={(v) => patch('gMobile', v)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <TextField label="Phone" value={f.gLandline} onChange={(v) => patch('gLandline', v)} />
              <SelectField label="Occupation" value={f.gOccupation} onChange={(v) => patch('gOccupation', v)} options={opt.occupations && opt.occupations.length ? opt.occupations : ['Select', 'Business', 'Service', 'Professional', 'Government']} />
            </div>
            <TextField label="Income" value={f.gIncome} onChange={(v) => patch('gIncome', v)} type="number" placeholder="0.00" />
          </div>
        </FormCard>

        <FormCard title="Mother / Guardian 2">
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <TextField label="Name" value={f.g2Name} onChange={(v) => patch('g2Name', v)} />
              <SelectField label="Relation" value={f.g2Relation} onChange={(v) => patch('g2Relation', v)} options={opt.relations && opt.relations.length ? opt.relations : ['Select', 'Mother', 'Father', 'Guardian']} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <CheckboxField label="Email" checked={f.g2SendEmail} onChange={(v) => patch('g2SendEmail', v)} />
                <TextField label="Email" value={f.g2Email} onChange={(v) => patch('g2Email', v)} type="email" />
              </div>
              <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                <CheckboxField label="Mobile" checked={f.g2SendSms} onChange={(v) => patch('g2SendSms', v)} />
                <TextField label="Mobile No" value={f.g2Mobile} onChange={(v) => patch('g2Mobile', v)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <TextField label="Phone" value={f.g2Landline} onChange={(v) => patch('g2Landline', v)} />
              <SelectField label="Occupation" value={f.g2Occupation} onChange={(v) => patch('g2Occupation', v)} options={opt.occupations && opt.occupations.length ? opt.occupations : ['Select', 'Business', 'Service', 'Professional', 'Government']} />
            </div>
            <TextField label="Income" value={f.g2Income} onChange={(v) => patch('g2Income', v)} type="number" placeholder="0.00" />
          </div>
        </FormCard>

        <FormCard title="Remarks">
          <TextAreaField label="Remark" value={f.particulars} onChange={(v) => patch('particulars', v)} placeholder="Enter remarks..." />
        </FormCard>
      </div>

      <div className="space-y-6 lg:col-span-5">
        <FormCard title="Address">
          <div className="space-y-3.5">
            <TextField label="Line 1" value={f.addressLine1} onChange={(v) => patch('addressLine1', v)} />
            <TextField label="Line 2" value={f.addressLine2} onChange={(v) => patch('addressLine2', v)} />
            <TextField label="Line 3" value={f.addressLine3} onChange={(v) => patch('addressLine3', v)} />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="City" value={f.city} onChange={(v) => patch('city', v)} />
              <TextField label="Pin Code" value={f.pinCode} onChange={(v) => patch('pinCode', v)} />
            </div>
            <SelectField label="State" value={f.state} onChange={(v) => patch('state', v)} options={opt.states && opt.states.length ? opt.states : ['Select', 'Maharashtra', 'Delhi', 'Gujarat', 'Karnataka']} />
            <SelectField label="Place" value={f.place} onChange={(v) => patch('place', v)} options={opt.places && opt.places.length ? opt.places : ['Select', 'Mumbai', 'Pune', 'Thane', 'Navi Mumbai']} />
          </div>
        </FormCard>

        <FormCard title="School / College">
          <div className="space-y-3.5">
            <SelectField label="Name" value={f.pSchool} onChange={(v) => patch('pSchool', v)} options={opt.schools && opt.schools.length ? opt.schools : ['Select', 'St. Xavier High School', 'National Public School']} />
            <SelectField label="Prev.Course" value={f.courseStd} onChange={(v) => patch('courseStd', v)} options={opt.courses_previous && opt.courses_previous.length ? opt.courses_previous : ['Select', 'Standard X', 'Standard XI']} />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Maths" value={f.marks} onChange={(v) => patch('marks', v)} type="number" placeholder="0" />
              <TextField label="Science" value={f.science} onChange={(v) => patch('science', v)} type="number" placeholder="0" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Marks %" value={f.marksPercent} onChange={(v) => patch('marksPercent', v)} type="number" placeholder="0.00" />
              <SelectField label="Status" value={f.academicStatus || 'Passed'} onChange={(v) => patch('academicStatus', v)} options={['Passed', 'Failed', 'Awaiting']} />
            </div>
            <SelectField label="Board" value={f.prevBoard} onChange={(v) => patch('prevBoard', v)} options={opt.boards && opt.boards.length ? opt.boards : ['Select', 'State Board', 'CBSE', 'ICSE']} />
            <SelectField label="Medium" value={f.medium} onChange={(v) => patch('medium', v)} options={opt.mediums && opt.mediums.length ? opt.mediums : ['Select', 'English', 'Hindi', 'Marathi']} />
          </div>
        </FormCard>

        <FormCard title="Photo">
          <PhotoUploadField label="Student Photo" value={f.photoUrl} onChange={(v) => patch('photoUrl', v)} />
        </FormCard>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'student-detail':
        return isView ? renderReadOnlyProfile() : renderEditableStudentDetail()
      case 'course':
        return <CourseTab form={f} patchForm={isView ? null : patch} readOnly={isView} options={opt} />
      case 'fees':
        return <FeesTab form={f} patchForm={isView ? null : patch} readOnly={isView} />
      case 'receipt':
        return (
          <ReceiptTab
            form={f}
            studentId={state.studentId}
            onSaveStudent={actions.handleSave}
          />
        )
      default:
        return null
    }
  }

  const studentName = `${f.firstName || ''} ${f.lastName || ''}`.trim() || 'New Student'

  const heading = isView
    ? 'Student Profile'
    : state.isEditMode
      ? 'Edit Student'
      : 'Create Student'

  return (
    <div className="mx-auto flex w-full max-w-[1360px] flex-col gap-6 pb-28">
      {/* ── Breadcrumb & Title ── */}
      <section>
        <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
          <span>Admission</span>
          <span className="material-symbols-outlined text-[10px]">chevron_right</span>
          <Link to="/admission/students/list" className="font-semibold text-primary hover:underline">
            Students
          </Link>
          {(state.isEditMode || isView) && (
            <>
              <span className="material-symbols-outlined text-[10px]">chevron_right</span>
              <span className="font-semibold text-primary">{studentName}</span>
            </>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <h1 className="text-2xl font-black text-on-surface">{heading}</h1>
          {isView && state.studentId && (
            <button
              type="button"
              onClick={() => {
                actions.setViewMode('edit')
                navigate(`/admission/students/entry?id=${state.studentId}&mode=edit&tab=${activeTab}`)
              }}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">edit</span>
              Edit Student
            </button>
          )}
        </div>
      </section>

      {/* ── Tabs Navigation ── */}
      <section>
        <StudentTabs activeTab={activeTab} onTabChange={setActiveTab} studentId={state.studentId} mode={mode} />
      </section>

      {/* ── Active Tab Content ── */}
      <section>{renderTabContent()}</section>

      {/* ── Audit Info Bar ── */}
      {(f.createdBy || f.updatedBy) && (
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-outline-variant/15 bg-surface-container-low px-5 py-3 text-xs text-on-surface-variant">
          {f.createdBy && (
            <span>
              Created by <strong className="text-on-surface">{f.createdBy}</strong> on{' '}
              <span className="font-medium text-on-surface">{f.createdDate}</span>
            </span>
          )}
          {f.updatedBy && state.isEditMode && (
            <span>
              &bull; Updated by <strong className="text-on-surface">{f.updatedBy}</strong> on{' '}
              <span className="font-medium text-on-surface">{f.updatedDate}</span>
            </span>
          )}
        </div>
      )}

      {/* ── Shared ERP Form Footer Bar ── */}
      <AppFormFooterBar
        cancelPath="/admission/students/list"
        cancelLabel={isView ? 'Back to List' : 'Cancel'}
        showSaveAndContinue={!isView}
        continueLabel="Save & Continue"
        onSaveContinue={async () => {
          const result = await actions.handleSave()
          if (result && (result.id || result.success)) {
            const nextTab = getNextTab()
            const id = result.id || state.studentId
            navigate(`/admission/students/entry?id=${id}&mode=edit&tab=${nextTab}`)
          }
        }}
        saveLabel={isView ? 'Edit Student' : 'Save & Exit'}
        onSaveExit={
          isView
            ? () => {
                actions.setViewMode('edit')
                navigate(`/admission/students/entry?id=${state.studentId}&mode=edit&tab=${activeTab}`)
              }
            : async () => {
                const result = await actions.handleSave()
                if (result && result.success !== false) {
                  navigate('/admission/students/list')
                }
              }
        }
        isSaving={state.isSaving}
      />
    </div>
  )
}
